import type { ThunkAction, ThunkDispatch } from 'redux-thunk';
import type { Option } from 'react-multi-select-component';

/* Shared type module for the Costing TODO feature. Mirrors the sibling Samples/MyTodo
   convention: domain shapes, slice state, action union, a narrow root state and component
   props all live here, because the four components plus the reducer, the columns hook, the
   cell renderers and the Excel module all need the same row shapes. */

/**
 * Response envelope returned by the services/ layer. The axios interceptor resolves
 * response.data, so callers receive this object rather than an AxiosResponse.
 */
export interface ApiResponse<T = unknown> {
  code?: number;
  message?: string;
  data?: T;
}

/** A `{ _id, name }` reference as embedded by the API in populated fields. */
export interface NamedRef {
  _id?: string;
  name?: string;
}

/** A populated Sample Ref, labelled by its sample type and number (`label`) rather than a `name`. */
export interface SampleRefNamed {
  _id?: string;
  sampleNumber?: string;
  label?: string;
}

/* ------------------------------------------------------------------ action types */

export const CostingType = {
  SET_COSTING_REQUESTS_TODOLIST: 'SET_COSTING_REQUESTS_TODOLIST',
  SET_COSTING_TODO_LIST_COUNT: 'SET_COSTING_TODO_LIST_COUNT',
  SET_SELECTED_IDS_ARRAY: 'SET_SELECTED_IDS_ARRAY',
  SET_FILTER_SORT_DATA: 'COSTING_TODO_SET_FILTER_SORT_DATA',
  GET_MAIN_SAMPLES: 'GET_MAIN_SAMPLES',
  CATEGORY: 'CATEGORY',
  GET_OTHER_SAMPLES: 'GET_OTHER_SAMPLES',
  SET_TODO_LIST_COUNT: 'SET_TODO_LIST_COUNT',
  SUPPLIER_BRAND: 'SUPPLIER_BRAND',
  SET_COSTING_STATS: 'SET_COSTING_STATS',
  PATCH_COSTING: 'COSTING_TODO_PATCH_COSTING',
  SET_COSTING_SAVE_STATE: 'COSTING_TODO_SET_SAVE_STATE',
} as const;

export type CostingActionTypeValue = (typeof CostingType)[keyof typeof CostingType];

export default CostingType;

/* ------------------------------------------------------------------ domain shapes */

/**
 * One entry of `expandedData[].landedData.landedInfo[]`. `key` is matched against a template
 * column's `type` by getLandedValue: 'percentage' reads calculatedValue, anything else tempValue.
 */
export interface LandedInfoEntry {
  _id?: string;
  key?: string;
  type?: string;
  title?: string;
  aliasDisplayName?: string;
  isActive?: boolean;
  value?: string | number;
  tempValue?: string | number;
  calculatedValue?: string | number;
}

/**
 * Landed cost figures for one colourway/size combination. Beyond the fixed fields the API
 * returns an arbitrary `<aliasName>` / `<aliasName>CalculatedValue` pair per template column,
 * which is why the index signature is required.
 */
export interface LandedData {
  quantityPerSizCol?: string | number;
  pricePerUnitInco?: string | number;
  totalInco?: string | number;
  pricePerUnitIncoConverted?: string | number;
  totalIncoConverted?: string | number;
  landedInfo?: LandedInfoEntry[];
  [aliasName: string]: string | number | LandedInfoEntry[] | undefined;
}

/** One entry of `costing.expandedData[]` — a colourway/size/length combination. */
export interface CostingExpandedData {
  colourway?: NamedRef;
  size?: string;
  length?: NamedRef;
  landedData?: LandedData;
}

/**
 * One combination's price and quantity inputs (BM-9220).
 *
 * These are the only calculation figures that do not live on a landed row, so they travel beside
 * `expandedData` instead of the whole `calcContext` the payload used to carry. The list endpoint
 * seeds them and the save response replaces them.
 */
export interface CostingCombination {
  colourwayId: string | null;
  size: string | null;
  lengthId: string | null;
  pricePerUnit: string;
  quantityPerSizCol: string;
}

/** A costing row as returned by getSampleRequestsTodoListService. */
export interface CostingRecord {
  _id?: string;
  number?: string;
  description?: string;
  image?: string;
  reference?: string;
  devOrderId?: string;
  status?: string;
  isAverageCosting?: boolean;
  isDetailedCosting?: boolean;
  markAsComplete?: boolean;
  /**
   * Per-combination price and quantity inputs. Replaced the far larger `calcContext` when the
   * calculation moved to the server; absent on responses predating that.
   */
  combinations?: CostingCombination[];
  targetCost?: string | number;
  brandId?: string | NamedRef;
  subBrandId?: string | NamedRef;
  seasonId?: string | NamedRef;
  month?: string;
  year?: string | number;
  catOneId?: string | NamedRef;
  catTwoId?: string | NamedRef;
  catThreeId?: string | NamedRef;
  catFourId?: string | NamedRef;
  supplierType?: string | NamedRef;
  /** Factory ids chosen for this product at creation; the Factory dropdown never offers others. */
  supplierFactories?: string[];
  sampleRef?: SampleRefNamed;
  supplier?: { supplierName?: string };
  comments?: { message?: string };
  createdBy?: string;
  createdAt?: string;
  modifiedBy?: string;
  updatedAt?: string;
  costColourways?: NamedRef[];
  costSizes?: string[];
  costLengths?: NamedRef[];
  expandedData?: CostingExpandedData[];
  landedExchangeRateInformation?: { companyCurrency?: string };
  /* The reference fields arrive populated as `{ _id, name }` from the list API but hold a plain id
     string once an inline edit has written them, so both shapes are modelled. */
  productInfo?: {
    reference?: string;
    currency?: string;
    shipmentMethod?: string;
    moqNeeded?: string | number;
    targetCost?: string | number;
    supplierId?: NamedRef | string | null;
    factoryId?: NamedRef | string | null;
    incoterms?: NamedRef | string | null;
  };
  productTemplate?: { costingInfo?: Array<{ _id?: string; value?: string | number }> };
}

/** A `costingInfo[]` field on a costing template. Positions 0-9 drive the default column labels. */
export interface CostingInfoField {
  _id?: string;
  title?: string;
  aliasDisplayName?: string;
  position?: number;
  isDefault?: boolean;
  type?: string;
}

/**
 * Shared shape of topColumnArr / defaultColumns / bottomColumnArr entries on a template.
 * `aliasName` and `title` are required: aliasName keys every landedData lookup, and title is
 * both pushed into the Excel header row and matched back against it to build the summary row.
 */
export interface TemplateColumn {
  aliasName: string;
  title: string;
  aliasDisplayName?: string;
  isActive?: boolean;
  type?: string;
  tempValue?: string | number;
}

/** `landedInfo[]` on a template — distinct from the per-row LandedInfoEntry. */
export interface TemplateLandedInfo {
  _id?: string;
  title: string;
  aliasDisplayName?: string;
  isActive?: boolean;
  type?: string;
  tempValue?: string | number;
}

export interface CostingTemplate {
  _id?: string;
  name?: string;
  isCostingInfoActive?: boolean;
  costingInfo?: CostingInfoField[];
  topColumnArr?: TemplateColumn[];
  defaultColumns?: TemplateColumn[];
  bottomColumnArr?: TemplateColumn[];
  landedInfo?: TemplateLandedInfo[];
}

/**
 * Aggregate figures behind the floating header badges.
 *
 * `sums` and `averages` are deliberately REQUIRED rather than optional. The JS reads
 * `averageTotalCal?.sums[alias]`, which throws when the payload omits `sums`; declaring them
 * optional would force a `?.` that silently swallows that throw and changes behaviour.
 * Preserving the throw is intentional under the port's bug-for-bug fidelity rule.
 */
export interface CostingStats {
  sums: Record<string, number | undefined>;
  averages: Record<string, number | undefined>;
}

/** MultiSelect option widened with the id/metadata keys createOptions() spreads onto each entry. */
export interface CostingSelectOption extends Option {
  /** `string | number` because createOptions() spreads the source item verbatim, and the year
   *  list it is fed by getYears() carries a numeric `_id`. */
  _id?: string | number;
  name?: string;
  brandId?: string;
  categoryOneId?: string;
  categoryTwoId?: string;
  categoryThreeId?: string;
  isUsedProductType?: Array<string | NamedRef>;
  isUsedCat1?: string[];
  productType?: Array<string | NamedRef>;
  firstName?: string;
  lastName?: string;
  companies?: Array<{ supplierName?: string }>;
}

/* ------------------------------------------------------------------ filter values */

/**
 * The `costingFilterValues` slice.
 *
 * The first block mirrors `initialState` exactly. The second block is the set of keys that
 * SET_FILTER_SORT_DATA merges in at runtime but that `initialState` never declares — they are
 * OPTIONAL on purpose. Seeding them with `[]` in the reducer would change the request wire
 * format on every call made before a filter is applied (axios omits undefined, but serialises
 * an empty array), so the absence is load-bearing.
 */
export interface CostingFilterValues {
  selectedcosting_Season: string[];
  selectedcosting_Year: string[];
  selectedcosting_Brand: string[];
  selectedcosting_Supplier: string[];
  selectedcosting_Type: string;
  selectedcosting_Category1: string[];
  selectedcosting_Category2: string[];
  selectedcosting_Category3: string[];
  selectedcosting_Category4: string[];
  selectedcosting_Section: string;
  /** Union preserved: six call sites guard with Array.isArray before use. */
  selectedcosting_Status: string[] | string;
  searchText: string;
  pageIndex: number;
  pageSize: number;
  sortOrder: string;
  sortPropertyName: string;
  loading: boolean;
  selectedLicensor: string[];
  selectedLicense: string[];
  selectedLikeness: string[];

  supplier?: string[] | undefined;
  colourway?: string[] | undefined;
  currency?: string[] | undefined;
  incoterms?: string[] | undefined;
  sampleRef?: string[] | undefined;
  selectedBrand?: string[] | undefined;
  selectedSubBrand?: string[] | undefined;
  selectedSeason?: string[] | undefined;
  selectedYear?: string[] | undefined;
  selectedMonth?: string[] | undefined;
  selectedProductType?: string[] | undefined;
  selectedCat1?: string[] | undefined;
  selectedCat2?: string[] | undefined;
  selectedCat3?: string[] | undefined;
  selectedCat4?: string[] | undefined;
  selectedCompanyUser?: string[] | undefined;
  selectedDesigner?: string[] | undefined;
  selectedCoo?: string[] | undefined;
  productGroupId?: string | null | undefined;
  costingTemplateId?: string | null | undefined;
}

export type CostingFilterKey = keyof CostingFilterValues;

/** Query payload accepted by the costing list and stats endpoints. */
export interface CostingListParams {
  type?: string | undefined;
  pageIndex?: number | undefined;
  pageSize?: number | undefined;
  totalRecords?: number | undefined;
  searchText?: string | undefined;
  isScrolled?: boolean | undefined;
  requestId?: string | undefined;
  sortOrder?: string | undefined;
  sortPropertyName?: string | undefined;
  costingTemplateId?: string | null | undefined;
  supplierId?: string | undefined;
  supplier?: string[] | undefined;
  colourway?: string[] | undefined;
  currency?: string[] | undefined;
  incoterms?: string[] | undefined;
  status?: string[] | string | undefined;
  brand?: string[] | undefined;
  subBrand?: string[] | undefined;
  sampleRef?: string[] | undefined;
  season?: string[] | undefined;
  year?: string[] | undefined;
  month?: string[] | undefined;
  productType?: string[] | undefined;
  catOne?: string[] | undefined;
  catTwo?: string[] | undefined;
  catThree?: string[] | undefined;
  catFour?: string[] | undefined;
  createdBy?: string[] | undefined;
  designer?: string[] | undefined;
  COO?: string[] | undefined;
  productGroupId?: string | null | undefined;
  licensorId?: string[] | undefined;
  licenseId?: string[] | undefined;
  likenessId?: string[] | undefined;
  costingIds?: string[] | undefined;
  downloadType?: string | undefined;
  timezone?: string | undefined;
}

/* ------------------------------------------------------------------ slice + actions */

/** Per-costing save state surfaced on the grid while an inline edit is in flight. */
export type CostingSaveStatus = 'saving' | 'saved' | 'error';

export interface CostingTodoListState {
  costingListData: CostingRecord[];
  costingSelectedIds: string[];
  costingTodoList: CostingRecord[];
  mainSamples: unknown[];
  costingFilterValues: CostingFilterValues;
  costingListTotalCount: number;
  supplierBrandsList: NamedRef[];
  averageTotalCal: CostingStats | null;
  currentRequestId: string | null;
  /** Save status keyed by costing id; a costing absent from the map is idle. */
  costingSaveState: Record<string, CostingSaveStatus>;
}

export interface SetCostingRequestsTodolistPayload {
  data: CostingRecord[];
  totalCount: number;
  searchText?: string | undefined;
  isScrolled?: boolean | undefined;
  averageTotalCal?: CostingStats | undefined;
  requestId?: string | undefined;
}

export interface SetCostingRequestsTodolistAction {
  type: typeof CostingType.SET_COSTING_REQUESTS_TODOLIST;
  payload: SetCostingRequestsTodolistPayload;
}
export interface SetSelectedIdsArrayAction {
  type: typeof CostingType.SET_SELECTED_IDS_ARRAY;
  payload: string[];
}
export interface SetFilterSortDataAction {
  type: typeof CostingType.SET_FILTER_SORT_DATA;
  payload: Partial<CostingFilterValues>;
}
export interface GetMainSamplesAction {
  type: typeof CostingType.GET_MAIN_SAMPLES;
  payload: unknown[];
}
export interface SupplierBrandAction {
  type: typeof CostingType.SUPPLIER_BRAND;
  payload: NamedRef[];
}
export interface SetCostingStatsAction {
  type: typeof CostingType.SET_COSTING_STATS;
  payload: CostingStats;
}
/* Dispatched by thunks but deliberately unhandled by the reducer — declared so the dispatch
   sites type-check without widening the union to AnyAction. */
export interface SetCostingTodoListCountAction {
  type: typeof CostingType.SET_COSTING_TODO_LIST_COUNT;
  payload: { totalCount: number; type?: string | undefined };
}
export interface CategoryAction {
  type: typeof CostingType.CATEGORY;
  payload: Array<{ label: string; value: string }>;
}
export interface GetOtherSamplesAction {
  type: typeof CostingType.GET_OTHER_SAMPLES;
  payload: unknown;
}
/** Replaces one costing in the loaded page after an inline edit has been recomputed. */
export interface PatchCostingAction {
  type: typeof CostingType.PATCH_COSTING;
  payload: { costingId: string; costing: CostingRecord };
}
export interface SetCostingSaveStateAction {
  type: typeof CostingType.SET_COSTING_SAVE_STATE;
  payload: { costingId: string; status: CostingSaveStatus | null };
}

export type CostingAction =
  | SetCostingRequestsTodolistAction
  | SetSelectedIdsArrayAction
  | SetFilterSortDataAction
  | GetMainSamplesAction
  | SupplierBrandAction
  | SetCostingStatsAction
  | SetCostingTodoListCountAction
  | CategoryAction
  | GetOtherSamplesAction
  | PatchCostingAction
  | SetCostingSaveStateAction;

/**
 * Narrow root state. There is no global RootState in this app; each feature declares the
 * slices it reads (the MyTodo/Samples convention). Never import this from reducers/index.js —
 * that file imports this module's reducer, so the dependency must stay one-way.
 */
export interface CostingRootState {
  CostingTodoListData: CostingTodoListState;
  CostingTemplateData: { costingTemplateList?: CostingTemplate[] };
  UserDetails: {
    userInfo: { userId?: string };
    isSupplierLoggedIn?: boolean;
    isCompanyUserLoggedIn?: boolean;
    userRole?: string;
  };
  Users: { logInRights?: Record<string, unknown> };
  SystemMastersDetails: {
    subBrandAccess?: boolean;
    /* Carries each sub-brand's own currency, which the retail columns are denominated in. */
    subBrandList?: Array<{ _id?: string; name?: string; currency?: string }>;
    multiSubBrandList?: Array<{ _id?: string; name?: string; brandId?: string }>;
    licensorList?: NamedRef[];
    licenseList?: NamedRef[];
    likenessList?: NamedRef[];
  };
  ColorLibraryDetails: { ColorLibList?: NamedRef[] };
}

/* State is `unknown` rather than CostingRootState because no thunk in this slice reads
   getState, and a concrete state type makes the dispatch generic contravariantly incompatible
   with the untyped .js thunks these components also dispatch. The action union is kept so the
   dispatch calls inside Costing.action.ts are still payload-checked. */
export type CostingThunkAction<R = void> = ThunkAction<R, unknown, unknown, CostingAction>;
export type CostingDispatch = ThunkDispatch<unknown, unknown, CostingAction>;

/* ------------------------------------------------------------------ grid rows */

/* Declared with `type` rather than `interface` on purpose: BombiixAGGrid constrains its row
   generic to `Record<string, unknown> & {...}`, and TypeScript only grants the implicit string
   index signature to type aliases. An interface here fails the constraint. Verified by
   compiler spike before this module was written. */

/** A costing header row. `hasExpanded` means the record carries more than one expandedData entry. */
export type CostingParentRow = {
  __cagId: string;
  __rowType: 'parent';
  parentIndex: number;
  cost: CostingRecord;
  hasExpanded: boolean;
  rowOneData: CostingExpandedData | undefined;
  __showChevron: boolean;
};

/** One expanded colourway/size line rendered beneath its parent. */
export type CostingChildRow = {
  __cagId: string;
  __rowType: 'child';
  parentIndex: number;
  cost: CostingRecord;
  expData: CostingExpandedData;
  landedInforData: LandedInfoEntry[] | undefined;
};

export type CostingGridRow = CostingParentRow | CostingChildRow;

/* ------------------------------------------------------------------ component props */

export interface CostingTODOTableProps {
  activeCostingTemplate: string | null;
  isDownloadLoading?: boolean | undefined;
  getSelectedCount?: ((ids: string[]) => void) | undefined;
  getSelectedIdsWithDevIds?: ((rows: Array<{ costingId?: string | undefined; devOrderId?: string | undefined }>) => void) | undefined;
  /** Called after inline edits are persisted, so the page can refresh the header stat badges. */
  onCostingsSaved?: (() => void) | undefined;
  /* Declared but unused by the table today. Costing.tsx passes `loading` and `onClick`, and the
     table historically destructured `exportFiles`/`isExportLoading` without referencing them.
     Kept on the prop surface so neither side's JSX changes; see IMPLEMENTATION_NOTES.md D1-D4. */
  loading?: boolean | undefined;
  onClick?: ((type: string) => void) | undefined;
  exportFiles?: ((type: string) => void) | undefined;
  isExportLoading?: boolean | undefined;
}

export interface ProductCostingFilterProps {
  activeCostingTemplate: string | null;
}

export interface CostingCommentModalProps {
  onOpen: boolean;
  toggle: () => void;
  addCostingComment: (commentMsg: string) => void | Promise<void>;
  commentLoading?: boolean | undefined;
}
